<script setup>
import { ref, computed, defineAsyncComponent } from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import { useCmsUiVisible } from '@/composables/useCmsUiVisible.js'
import { isReviewLive } from '@/cms/reviewGate.js'
import {
  SITE_URL,
  DEFAULT_OG_IMAGE,
  DEFAULT_OG_IMAGE_WIDTH,
  DEFAULT_OG_IMAGE_HEIGHT,
  DEFAULT_OG_IMAGE_TYPE,
  DEFAULT_OG_IMAGE_ALT,
  withTrailingSlash
} from '@/utils/siteUrl'
import Breadcrumbs from '@/components/Breadcrumbs.vue'
import SplitHeaderBlock from '@/components/SplitHeaderBlock.vue'
import Icon from '@/components/Icon.vue'
import { trainingModeIcon } from '@/utils/trainingModeIcon'

const isCmsEnabled = import.meta.env.MODE === 'cms'
const CmsEditorModal = isCmsEnabled
  ? defineAsyncComponent(() => import('@/cms/components/CmsEditorModal.vue'))
  : null

const { cmsUiVisible } = useCmsUiVisible()
const showCmsChrome = computed(() => isCmsEnabled && cmsUiVisible.value)

const route = useRoute()

const modules = import.meta.glob('/src/content/training/*.md', { eager: true })
const slug = computed(() => route.params.slug)

const isEditing   = ref(false)
const contentPath = computed(() => `/src/content/training/${slug.value}.md`)

const frontmatter = computed(() => {
  const mod = modules[contentPath.value]
  if (!mod) return null
  return {
    title: mod.title,
    format: mod.format,
    mode: mod.mode,
    duration: mod.duration,
    audience: mod.audience,
    providers: mod.providers || [],
    publishedAt: mod.publishedAt || null,
    reviewStatus: mod.reviewStatus || '',
  }
})

const modeIcon = computed(() => trainingModeIcon(frontmatter.value?.mode))

const isPublished = computed(() => {
  if (!frontmatter.value) return true
  if (import.meta.env.DEV) return true
  const p = frontmatter.value.publishedAt
  return (!p || new Date(p) <= new Date()) && isReviewLive(frontmatter.value.reviewStatus)
})

const breadcrumbs = computed(() => [
  { label: 'Home', to: '/' },
  { label: 'Training', to: '/resources/training' },
  { label: frontmatter.value?.title || 'Course' },
])

const baseUrl = import.meta.env.VITE_APP_BASE_URL || SITE_URL

// Training courses have no per-page image field, so this always uses the
// sitewide default (jk-35) — dimensions are known, so always emitted.
useHead(computed(() => {
  const title = frontmatter.value ? frontmatter.value.title : 'Training'
  const description = frontmatter.value?.audience || ''
  const url = `${baseUrl}${withTrailingSlash(`/resources/training/${slug.value}`)}`
  const ogImageAbs = `${baseUrl}${DEFAULT_OG_IMAGE}`
  return {
    title,
    meta: [
      { name: 'description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: url },
      { property: 'og:image', content: ogImageAbs },
      { name: 'twitter:image', content: ogImageAbs },
      { property: 'og:image:width', content: DEFAULT_OG_IMAGE_WIDTH },
      { property: 'og:image:height', content: DEFAULT_OG_IMAGE_HEIGHT },
      { property: 'og:image:type', content: DEFAULT_OG_IMAGE_TYPE },
      { property: 'og:image:alt', content: DEFAULT_OG_IMAGE_ALT },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
    ],
    link: [{ rel: 'canonical', href: url }],
  }
}))

const lazyModules = import.meta.glob('/src/content/training/*.md')
const MarkdownContent = computed(() => {
  const key = `/src/content/training/${slug.value}.md`
  if (!lazyModules[key]) return null
  return defineAsyncComponent(lazyModules[key])
})
</script>

<template>
  <div class="training-detail container">
    <template v-if="!frontmatter">
      <h1>Training page not found</h1>
      <RouterLink to="/resources/training/">← Back to training</RouterLink>
    </template>

    <template v-else-if="!isPublished">
      <p class="not-published">This course is not yet available.</p>
      <div class="back"><RouterLink to="/resources/training/">← Back to training</RouterLink></div>
    </template>

    <template v-else>
      <Breadcrumbs :items="breadcrumbs" />
      <CmsEditorModal v-if="isCmsEnabled && isEditing" :content-path="contentPath" @close="isEditing = false" />

      <SplitHeaderBlock class="layout">
        <template #header>
          <div v-if="showCmsChrome" class="edit-row">
            <button class="edit-inline" @click="isEditing = true">Edit</button>
          </div>

          <h1 class="title">{{ frontmatter.title }}</h1>

          <ul class="facts">
            <li v-if="frontmatter.audience" class="item">
              <Icon name="group" size="sm" color="var(--color-text-muted)" />
              <span>{{ frontmatter.audience }}</span>
            </li>
            <li v-if="frontmatter.mode" class="item">
              <Icon :name="modeIcon" size="sm" color="var(--color-text-muted)" />
              <span>{{ frontmatter.mode }}</span>
            </li>
            <li v-if="frontmatter.duration" class="item">
              <Icon name="schedule" size="sm" color="var(--color-text-muted)" />
              <span>{{ frontmatter.duration }}</span>
            </li>
          </ul>
        </template>

        <component :is="MarkdownContent" v-if="MarkdownContent" class="body" />

        <div v-if="frontmatter.providers.length" class="providers">
          <ul>
            <li v-for="provider in frontmatter.providers" :key="provider.label">
              <img v-if="provider.logo" class="logo" :src="provider.logo" :alt="provider.label" loading="lazy" />
              <a v-if="provider.href" :href="provider.href" target="_blank" rel="noopener noreferrer">{{ provider.cta || provider.label }} ↗</a>
              <span v-else>{{ provider.cta || provider.label }}</span>
            </li>
          </ul>
        </div>

        <div class="back"><RouterLink to="/resources/training/">← Back to training</RouterLink></div>
      </SplitHeaderBlock>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.training-detail {
  // No max-width override — .container (1440px) applies directly now.
  // Was $content-prose (760px), a narrow single-column article layout;
  // replaced with the shared SplitHeaderBlock grid (see .layout below).
  padding-block: $space-8;

  .breadcrumbs { margin-bottom: $space-5; }

  // No margin here — SplitHeaderBlock's own .header already applies a
  // flex gap between its children (edit-row/title/facts).
  .edit-row {
    display: flex;
    justify-content: flex-end;
  }

  .edit-inline {
    background: none;
    border: 1px solid var(--color-border);
    border-radius: $radius-sm;
    color: var(--color-text-muted);
    cursor: pointer;
    font-family: $font-sans;
    font-size: $font-size-cap;
    font-weight: 600;
    padding: 2px $space-2;
    @include transition(color, border-color, background);

    &:hover         { color: var(--color-primary); border-color: var(--color-primary); }
    &:focus-visible { @include focus-ring; }
    &:active        { transform: translateY(1px); }
  }

  .title {
    // margin: 0 (not just margin-bottom) — the browser default UA top
    // margin on <h1> was stacking with the old chip row's own spacing,
    // pushing the header column out of line with the content column's
    // first line (the markdown body's own heading).
    margin: 0 0 $space-6;
    font-size: $font-size-h2;   // one step down from $font-size-h1 — read too large
    font-weight: 700;
    line-height: $lh-heading;
  }

  .facts {
    list-style: none;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: $space-3;
    padding: $space-4;
    background: var(--color-fill-subtle);
    border: 1px solid var(--color-border);
    border-radius: $radius;

    // Icon + text rows (jk-31 follow-up) — matches Training's own card
    // listing (TrainingCard.vue's .audience/.meta .item), instead of the
    // former plain "Label: value" bolded text.
    .item {
      display: flex;
      align-items: center;
      gap: $space-2;
      font-size: $font-size-sm;
      color: var(--color-text);
    }
  }

  .body {
    line-height: $lh-base;
    color: var(--color-text);

    // The markdown body's own first heading (e.g. "About the course") had
    // full default browser margin, throwing the content column out of line
    // with the header column's flush <h1> — zero it so both start level.
    > :first-child { margin: 0; }
  }

  .providers {
    margin-top: $space-5;
    padding-top: $space-5;
    border-top: 1px solid var(--color-border);

    h2 { margin-bottom: $space-3; }

    // margin/padding zeroed so the links sit flush left — list-style: none
    // removes the markers but not the browser's default left indent.
    ul { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: $space-4; }

    // Each provider row: logo left, "Enrol now through …" link right (jk-33).
    // align-items: center keeps the CTA vertically centred against the chip.
    li {
      display: flex;
      align-items: center;
      gap: $space-3;
    }

    // Light chip behind each provider mark — most logos are light-background
    // marks, so a chip that stays warm-white in BOTH themes keeps them legible
    // rather than washing out on the dark surface. Uses the compile-time SCSS
    // tokens ($color-surface-warm / $color-border), not the var(--…) custom
    // properties — those flip with data-theme and would turn the chip dark.
    // object-fit: contain preserves each logo's aspect ratio.
    .logo {
      flex: none;
      height: $space-8;
      width: auto;
      max-width: 160px;
      object-fit: contain;
      padding: $space-2;
      background: $color-surface-warm;
      border: 1px solid $color-border;
      border-radius: $radius-sm;
    }

    a {
      color: var(--color-primary);
      text-decoration: none;
      font-weight: 600;
      &:hover { text-decoration: underline; }
    }

    span { color: var(--color-text-muted); }
  }

  .not-published {
    padding: $space-10 0;
    color: var(--color-text-muted);
    font-style: italic;
  }

  .back {
    margin-top: $space-10;
    padding-top: $space-6;
    border-top: 1px solid var(--color-border);
    font-size: $font-size-sm;
    a { color: var(--color-primary); text-decoration: none; &:hover { text-decoration: underline; } }
  }
}
</style>
